import React, { useEffect, useMemo, useState } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import routes from '../../config/settings/routes'
import { useEffectOnce, useQuery, useQueryParam, useRadio, useViewport } from '@chilecompra/react-kit'
import { Button, Card, Typography, Toast, Spinner } from '@chilecompra/react-kit/components'
import AttachFiles from '../../components/Custom/AttachFiles'
import AttachedFiles from '../../components/Custom/AttachedFiles'
import BasicInformation from './QuoteStepTwo.basicInformation'
import Errors from '../../components/Custom/Errors'
import FinesPenalties from './QuoteStepTwo.finesPenalties'
import Notifications from './QuoteStepTwo.notifications'
import ProductsContainer from './QuoteStepTwo.productsContainer'
import { topPage } from '../../modules/utils/topPage'
import { onOpenPublishQuoteDialog } from '../PublishQuoteDialog/PublishQuoteDialog.actions'
import {
  onClearErrorsStepTwo,
  onDeleteFileThunk,
  onGetAttachedFilesThunk,
  onGetDownloadFileThunk,
  onGetRegionsThunk,
  onGetUnitsMeasureThunk,
  onFragmentationAlertThunk,
  onCompanySizeFilterSuccess,
  onRegionNotificationSuccess,
  onRemoveSellerSuccess,
  onSaveFinesPenaltiesSuccess,
  onSaveRoughCopyThunk,
  onUploadFileThunk,
  onSetEnvironmentalValue,
  onSetSocialEconomicValue,
  onOpenMoreInfoDialog
} from './QuoteStepTwo.actions'
import {
  onRemoveAllSuccess,
  onRemoveProductSuccess,
  onSetProductsDraft,
  onUpdateProductsSavedSuccess
} from '../AddProductsServices/AddProductsServices.actions'
import {
  onGetQuoteByCodeThunk,
  onOpenQuoteSavedSatisfactoryDialog,
  onOpenQuoteSaveErrorDialog
} from '../QuoteStepOne/QuoteStepOne.actions'
import {
  Divider,
  Grid,
  LinkParticipant,
  MoreInformationLink,
  NotFoundImg,
  Radio,
  Tooltip,
  TooltipIcon,
  TooltipText
} from './QuoteStepTwo.styles'
import ProtectByFeatures from '../../components/Security/ProtectByFeatures'
import { REACT_APP_PARTICIPANT_LIST_URL } from '../../config/settings/environment'
import { PARTICIPANT_LIST_PREFIX, YES_OR_NO_OPTIONS } from '../../config/settings/constants'
import ProtectByProfile from '../../components/Security/ProtectByProfile'
import { getCookieFromDomain } from '../../modules/cookies'
import { inIframe } from '../../modules/utils/inIframe'
import { features } from '../../config/settings/features'

/**
 * The QuoteStepTwo's container.
 */
const QuoteStepTwo = () => {
  const userInfo = Object.fromEntries(new URLSearchParams(getCookieFromDomain('user_info')))

  const { productsSaved } = useSelector(state => state.addProductsService)
  const { openSaveDialog: openDraf, quote } = useSelector(state => state.quoteStepOne)
  const { authInfo } = useSelector(state => state.authorizationProvider)
  const {
    attachedFiles,
    companySizeFilter,
    error,
    errorAF,
    errors,
    filterRegionCode,
    filterRegionNotification,
    finesPenalties,
    fragmentationAlert,
    initData: { unitsMeasure },
    loading,
    loadingAF,
    sellersAdded,
    isEnvironmental,
    isSocialEconomic
  } = useSelector(state => state.quoteStepTwo)
  const { showPublishQuoteDialog: openPublished } = useSelector(state => state.publishQuoteDialog)
  const { open: openError } = useSelector(state => state.quoteRequestPublishedErrorDialog)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const query = useQuery()
  const quoteCode = useQueryParam('quote')

  const isInIframe = useMemo(() => {
    return inIframe()
  }, [])

  const [disabledSubmit, setDisabledSubmit] = useState(true)
  const [productsUpdate, setProductsUpdate] = useState({})
  const [regionValue, setRegionValue] = useState(-1)

  const {
    size: { isDownToTiny, isDownToMedium }
  } = useViewport()

  const [isEnvironmentalError, setIsEnvironmentalError] = useState('')
  const [isSocialEconomicError, setIsSocialEconomicError] = useState('')

  const showAttachmentsRestricted = features['attachments.restricted.to.saved.state']

  const { onChange: handleEnvironmentalChange, value: isEnvironmentalValue } = useRadio({
    changeCallback: updatedValue => dispatch(onSetEnvironmentalValue({ isEnvironmental: updatedValue })),
    overwriteCallback: () => isEnvironmental
  })
  const { onChange: handleSocialEconomicChange, value: isSocialEconomicValue } = useRadio({
    changeCallback: updatedValue => dispatch(onSetSocialEconomicValue({ isSocialEconomic: updatedValue })),
    overwriteCallback: () => isSocialEconomic
  })

  const handleCompanySizeFilter = (companySizeFilter, filterRegionCode, filterRegionNotification) => {
    dispatch(onCompanySizeFilterSuccess({ companySizeFilter, filterRegionCode, filterRegionNotification }))
  }

  const productsCode = productsSaved && productsSaved.map(x => x.productId)
  const errorNumberProviders = errors?.some(item => item.description === 'compraagil_error_solicitud_018')

  const handleSubmit = event => {
    event.preventDefault()
    const products = Object.values(productsUpdate)

    const isEnvironmentalEmpty = isEnvironmentalValue === ''
    const isSocialEconomicEmpty = isSocialEconomicValue === ''

    if (isEnvironmentalEmpty || isSocialEconomicEmpty) {
      setIsEnvironmentalError(isEnvironmentalEmpty ? 'Este campo es requerido' : '')
      setIsSocialEconomicError(isSocialEconomicEmpty ? 'Este campo es requerido' : '')

      return
    }

    batch(() => {
      dispatch(onUpdateProductsSavedSuccess({ products }))
      dispatch(onOpenPublishQuoteDialog())
    })
  }

  const handleSaveRoughCopy = () => {
    const products = Object.values(productsUpdate)

    batch(() => {
      dispatch(onUpdateProductsSavedSuccess({ products }))
      dispatch(onSaveFinesPenaltiesSuccess({ finesPenaltiesValue: finesPenalties }))
      dispatch(
        onSaveRoughCopyThunk({
          finesPenalties,
          callback: ({ response }) => {
            if (response === 'success') {
              return dispatch(onOpenQuoteSavedSatisfactoryDialog())
            }

            return dispatch(onOpenQuoteSaveErrorDialog())
          }
        })
      )
    })
  }

  const handleAddProductsServices = () => {
    query.set('quote', quoteCode)
    navigate({ pathname: routes.searchGoodsAndServices, search: query.toString() }, { replace: true })
  }

  const handleRemoveAll = () => {
    setProductsUpdate({})
    dispatch(onRemoveAllSuccess())
  }

  const handleRemoveProduct = productKey => {
    const updatedProducts = Object.values(productsUpdate)
      .filter(product => product.productKey !== productKey)
      .reduce((dictionary, product) => ({ ...dictionary, [product.productKey]: product }), {})
    setProductsUpdate(updatedProducts)

    const products = Object.values(productsUpdate)
    batch(() => {
      dispatch(onUpdateProductsSavedSuccess({ products }))
      dispatch(onRemoveProductSuccess({ productKey }))
    })
  }

  const handleDisabledSubmit = () => {
    const productsSavedValidate = Object.values(productsUpdate).some(
      product => product.quantity === null || product.unit === null || product.detail === ''
    )

    return productsSavedValidate
  }

  const handleUpdateProductsSaved = () => {
    const products = Object.values(productsUpdate)

    dispatch(onUpdateProductsSavedSuccess({ products }))
    handleAddProductsServices()
  }

  const handleGoBackStepOne = () => {
    dispatch(onClearErrorsStepTwo())
    query.set('quote', quoteCode)
    navigate({ pathname: routes.quoteStepOne, search: query.toString() }, { replace: true })
  }

  const handleDeleteFile = fileId => dispatch(onDeleteFileThunk({ fileId, quoteCode, ipUsuario: userInfo.host || '' }))
  const handleDownloadFile = ({ id, name }) => dispatch(onGetDownloadFileThunk({ id, name }))

  const handleRemoveSeller = ({ dni }) => {
    const sellersAddedUpdate = sellersAdded?.filter(seller => seller.dni !== dni)
    dispatch(onRemoveSellerSuccess({ sellersAdded: sellersAddedUpdate }))
  }

  const handleUploadFile = file => {
    dispatch(
      onUploadFileThunk({
        quoteCode,
        file
      })
    )
  }

  const handleSaveFinesPenalties = finesPenaltiesValue => dispatch(onSaveFinesPenaltiesSuccess({ finesPenaltiesValue }))

  const handleUpdatedStepTwoStore = () => {
    const products = Object.values(productsUpdate)
    batch(() => {
      dispatch(onUpdateProductsSavedSuccess({ products }))
      dispatch(onSaveFinesPenaltiesSuccess({ finesPenaltiesValue: finesPenalties }))
    })
    navigate(routes.quoteProvidersList)
  }

  useEffectOnce(() => {
    topPage(isInIframe)
    batch(() => {
      dispatch(onGetUnitsMeasureThunk())
      dispatch(onGetRegionsThunk())
      dispatch(onGetAttachedFilesThunk({ quoteCode }))
      Object.keys(quote).length === 0 && dispatch(onGetQuoteByCodeThunk({ code: quoteCode }))
    })
  })

  useEffect(() => {
    const productsUpdateValidate = Object.values(productsUpdate).length > 0
    if (isEnvironmentalValue !== '' && isSocialEconomicValue !== '' && productsUpdateValidate) {
      setDisabledSubmit(handleDisabledSubmit())
    }
  }, [isEnvironmentalValue, isSocialEconomicValue, productsUpdate])

  useEffect(() => {
    if (isEnvironmentalError && isEnvironmentalValue) {
      setIsEnvironmentalError('')
    }

    if (isSocialEconomicError && isSocialEconomicValue) {
      setIsSocialEconomicError('')
    }
  }, [isEnvironmentalError, isSocialEconomicError, isEnvironmentalValue, isSocialEconomicValue])

  useEffect(() => {
    const productsSavedValidate = Object.values(productsSaved).length === 0
    if (productsSavedValidate) {
      dispatch(onSetProductsDraft())
    }
    !finesPenalties && dispatch(onSaveFinesPenaltiesSuccess({ finesPenaltiesValue: Boolean(quote.finesPenalties) }))
  }, [quote])

  useEffect(() => {
    setProductsUpdate(() =>
      productsSaved.reduce((dictionary, product) => ({ ...dictionary, [product.productKey]: product }), {})
    )
    if (productsCode.length > 0) {
      dispatch(onFragmentationAlertThunk({ productsCode }))
    }
  }, [productsSaved])

  useEffect(() => {
    topPage(isInIframe, openPublished)
  }, [openPublished])

  useEffect(() => {
    topPage(isInIframe, openError)
  }, [openError])

  useEffect(() => {
    topPage(isInIframe, openDraf)
  }, [openDraf])

  useEffect(() => {
    if (regionValue !== -1) {
      dispatch(
        onRegionNotificationSuccess({
          filterRegionCode: regionValue,
          filterRegionNotification: regionValue > 0 ? 1 : regionValue
        })
      )
    } else {
      dispatch(
        onRegionNotificationSuccess({
          filterRegionCode: -1,
          filterRegionNotification: 0
        })
      )
    }
  }, [regionValue])

  return (
    <>
      <Grid container>
        <Grid item xs={12} md={8}>
          <Divider />
          <BasicInformation isDownToTiny={isDownToTiny} name={quote?.name} quoteCode={quoteCode} />
        </Grid>
      </Grid>
      {productsSaved.length > 0 ? (
        <ProductsContainer
          fragmentationAlert={fragmentationAlert}
          productsSaved={productsSaved}
          productsUpdate={productsUpdate}
          onRemoveAll={handleRemoveAll}
          onRemoveProduct={handleRemoveProduct}
          onUpdateProductsSaved={handleUpdateProductsSaved}
          setProductsUpdate={setProductsUpdate}
          unitsMeasureOption={unitsMeasure}
        />
      ) : (
        <Grid container margin="16px 0 24px">
          <Grid item xs={12} md={8} container>
            <Typography color="black1" fontSize="18px" fontWeight="bold" lineHeight="22px" variant="h4">
              Productos a cotizar
            </Typography>
            <Card color="blank" enableBorder margin="16px 0 32px">
              <Grid container justifyContent="center">
                <NotFoundImg alt="No se encontraron resultados" src="/assets/images/nofound.svg" />
              </Grid>
              <Typography
                align="center"
                color="black3"
                fontSize="14px"
                lineHeight="18px"
                margin="16px 0 0"
                variant="body2"
              >
                No tienes productos o servicios agregados
              </Typography>
              <Grid container justifyContent="center" margin="24px 0 0">
                <Button
                  disabled={authInfo.hasObserverRole}
                  color="primary"
                  onClick={handleAddProductsServices}
                  type="button"
                  variant="outlined"
                >
                  Agregar productos
                </Button>
              </Grid>
            </Card>
            <Divider />
          </Grid>
        </Grid>
      )}
      <ProtectByFeatures rule={features => features['notifications.providers']}>
        <Grid container>
          <Grid item xs={12} md={8}>
            <Notifications
              companySizeFilter={companySizeFilter}
              delivery={quote?.delivery}
              filterRegionCode={filterRegionCode}
              filterRegionNotification={filterRegionNotification}
              regionValue={regionValue}
              sellersAdded={sellersAdded}
              showError={errorNumberProviders}
              onCompanySizeFilter={handleCompanySizeFilter}
              onUpdatedStepTwoStore={handleUpdatedStepTwoStore}
              onRemoveSeller={handleRemoveSeller}
              setRegionValue={setRegionValue}
            />
            <Divider />
          </Grid>
        </Grid>
      </ProtectByFeatures>
      <Grid container margin="16px 0 24px">
        <Grid item xs={12} md={8} container>
          <AttachFiles
            label="Documento anexo"
            loading={loadingAF}
            error={errorAF}
            sm={3}
            subtitle={
              <>
                Puedes subir archivos que
                <br /> especifiquen tu solicitud (opcional)
              </>
            }
            onUploadFile={handleUploadFile}
            infoAlert={
              showAttachmentsRestricted ? (
                <Toast severity="info">
                  <strong> Adjuntos no editables tras publicación </strong> <br /> Una vez publicada la Compra Ágil, no
                  es posible agregar ni eliminar archivos adjuntos. Si necesitas modificar los adjuntos, deberás generar
                  un nuevo proceso de Compra Ágil
                </Toast>
              ) : null
            }
          />
          <Grid container margin="64px 0 0">
            {loadingAF && (
              <Grid container justifyContent="center">
                <Spinner />
              </Grid>
            )}
            {!loadingAF &&
              attachedFiles &&
              attachedFiles.length > 0 &&
              attachedFiles.map((files, i) => (
                <AttachedFiles
                  divider={attachedFiles.length - 1 !== i}
                  id={files.id}
                  key={files.id}
                  name={files.name}
                  onDeleteFile={handleDeleteFile}
                  onDownloadFile={handleDownloadFile}
                />
              ))}
          </Grid>
          <ProtectByFeatures rule={features => features['link.add.participants']}>
            <Divider />
          </ProtectByFeatures>
        </Grid>
      </Grid>
      <ProtectByProfile rule={userType => userType === 'Comprador'} fallback={() => null}>
        <ProtectByFeatures rule={features => features['link.add.participants']}>
          <Grid container margin="16px 0 24px">
            <Grid item xs={12} md={8} container>
              <Grid container margin="8px 0 30px">
                <Grid item xs={12} sm={3}>
                  <Typography fontSize="14px" lineHeight="18px" color="black1" fontWeight="bold" variant="body2">
                    Listado de participantes
                  </Typography>
                  <Typography
                    color="black1"
                    fontSize="12px"
                    italic
                    lineHeight="14px"
                    margin={isDownToTiny ? '0 0 12px' : '0'}
                    variant="body2"
                    padding="2px 0 0"
                  >
                    Puedes añadir participantes de este
                    <br /> proceso de compra.
                  </Typography>
                </Grid>
                <Grid item xs={12} sm>
                  <Grid container>
                    <Grid item xs={12}>
                      <LinkParticipant
                        textDecoration="underline"
                        target="_blank"
                        href={`${REACT_APP_PARTICIPANT_LIST_URL}/participantes/agregar/${PARTICIPANT_LIST_PREFIX}/${quoteCode}`}
                      >
                        Añadir participantes al listado
                      </LinkParticipant>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Divider />
            </Grid>
          </Grid>
        </ProtectByFeatures>
      </ProtectByProfile>

      <ProtectByProfile rule={userType => userType === 'Comprador'}>
        <Grid container margin="16px 0 24px">
          <Grid container item md={8} xs={12}>
            <Grid container margin="16px 0" spacing={2}>
              <Grid item sm={3} xs={12}>
                <Typography fontWeight="bold" margin="0 0 6px" variant="body2">
                  Compras sustentables
                </Typography>

                <Typography color="black1" italic margin={isDownToTiny ? '0 0 12px' : '0'} variant="subtitle">
                  Esta marca no altera el proceso de compra, pero es clave para medir y visualizar las compras
                  sustentables en el Estado, en línea con los objetivos de la nueva Ley de Compras Públicas.
                </Typography>
              </Grid>

              <Grid item sm xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography fontWeight="bold" margin="0 0 8px" variant="body2">
                      ¿Esta Compra Ágil considera requisitos medioambientales?
                      <Tooltip
                        content={
                          <TooltipText>
                            Se refiere a una compra que incluye requisitos que se enfocan en el equilibrio entre el uso
                            de recursos naturales y el impacto que las actividades tienen en el medio ambiente,
                            priorizando la eficiencia energética, el reciclaje de materiales y la reducción de la huella
                            ecológica. Es una contratación que integra prácticas que minimicen el daño al ecosistema y
                            promuevan el uso responsable de recursos renovables.
                          </TooltipText>
                        }
                      >
                        <TooltipIcon size="large" />
                      </Tooltip>
                    </Typography>

                    <Radio
                      direction="horizontal"
                      disabled={authInfo.hasObserverRole || loading}
                      error={isEnvironmentalError}
                      onChange={handleEnvironmentalChange}
                      options={YES_OR_NO_OPTIONS}
                      value={isEnvironmentalValue}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography fontWeight="bold" margin="0 0 8px" variant="body2">
                      ¿Esta Compra Ágil considera requisitos con impacto social o económico?
                      <Tooltip
                        content={
                          <TooltipText>
                            Se refiere a una compra que incluye requisitos que se enfocan en procurar mejores
                            condiciones sociales, laborales y económicas, buscando, junto con la generación de valor
                            económico, el fortalecimiento del tejido social, laboral y promuevan la justicia y bienestar
                            para las comunidades.
                          </TooltipText>
                        }
                      >
                        <TooltipIcon size="large" />
                      </Tooltip>
                    </Typography>

                    <Radio
                      direction="horizontal"
                      disabled={authInfo.hasObserverRole || loading}
                      error={isSocialEconomicError}
                      onChange={handleSocialEconomicChange}
                      options={YES_OR_NO_OPTIONS}
                      value={isSocialEconomicValue}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Toast severity="info">
                      Consulta más información sobre estos requisitos. <br />
                      <MoreInformationLink onClick={() => dispatch(onOpenMoreInfoDialog())}>
                        Ver más información
                      </MoreInformationLink>
                    </Toast>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Divider />
          </Grid>
        </Grid>
      </ProtectByProfile>

      <FinesPenalties finesPenalties={finesPenalties} onSaveFinesPenalties={handleSaveFinesPenalties} />
      {!errorNumberProviders && <Errors errors={errors} openDialog={openError} />}
      {error && (
        <Grid container margin="16px 0 24px">
          <Grid item xs={12}>
            <Toast severity="error" title="Lo sentimos, estamos presentando problemas con nuestro servidor.">
              En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos
              minutos más.
            </Toast>
          </Grid>
        </Grid>
      )}
      <Grid container margin="32px 0">
        <Grid item xs={12} md={8} container>
          <Grid container margin="0 0 32px">
            <Divider />
          </Grid>
          <Grid container>
            <Button onClick={handleGoBackStepOne} variant="outlined" width={isDownToMedium ? '100%' : undefined}>
              Volver
            </Button>
            <Grid item xs={12} md container justifyContent="flex-end">
              <Button
                disabled={disabledSubmit || loading || authInfo.hasObserverRole}
                margin={isDownToMedium ? '10px 0 0' : '0 12px 0 0'}
                onClick={handleSaveRoughCopy}
                variant="contained"
                width={isDownToMedium ? '100%' : '220px'}
              >
                Guardar Borrador
              </Button>
              <Button
                color="success"
                disabled={disabledSubmit || loading || authInfo.hasObserverRole}
                onClick={handleSubmit}
                margin={isDownToMedium ? '10px 0 0' : '0'}
                width={isDownToMedium ? '100%' : '230px'}
              >
                Publicar compra ágil
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}

export default QuoteStepTwo
